import { useState, useEffect } from "react";
import { format, startOfWeek, endOfWeek, eachDayOfInterval, addWeeks, subWeeks, startOfDay, addHours, isSameDay, parseISO } from "date-fns";
import {
  formatInCompanyTz,
  isSameCompanyDay,
  getCompanyHour,
  getCompanyMinute,
} from "@/lib/scheduleTime";
import { ChevronLeft, ChevronRight, Plus, Clock, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TruncatedTextTooltip } from "@/components/ui/truncated-text-tooltip";
import { useIsMobile } from "@/hooks/use-mobile";
import { useHasPermission } from "@/hooks/permissions/useHasPermission";
import { useAuth } from "@/hooks/useAuth";

import { LeaveOverlayBadge } from "./LeaveOverlayBadge";
import { PublicHolidayBadge } from "./PublicHolidayBadge";
import type { LeaveOverlay } from "@/services/leave/leaveOverlayTypes";
import type { PublicHolidayOverlay } from "@/hooks/usePublicHolidayOverlay";
import "./shift-animations.css";

interface Shift {
  id: string;
  profile_id: string | null;
  start_time: string;
  end_time: string;
  note: string | null;
  employee_name: string;
  status?: 'active' | 'open';
  offered_by_user_id?: string | null;
  offered_by_name?: string | null;
  swap_status?: string | null;
  shift_code?: string | null;
  is_all_day?: boolean;
  shift_code_info?: {
    code: string;
    name: string;
    color: string;
    category: string;
  } | null;
}

interface WeekViewProps {
  currentWeek: Date;
  onWeekChange: (date: Date) => void;
  shifts: Shift[];
  onTimeSlotClick: (date: Date, hour: number) => void;
  onShiftsUpdated: (updatedShifts: Shift[]) => void;
  profiles?: Array<{ id: string; first_name?: string; last_name?: string; full_name?: string }>;
  onShiftClick?: (shift: Shift) => void;
  onCreateShift?: (context: "empty-week" | "cell", weekStartISO?: string, weekEndISO?: string, initialDateISO?: string, initialStartHHmm?: string, initialEndHHmm?: string) => void;
  /** Approved leave overlays to display in All Day row (optional) */
  leaveOverlays?: LeaveOverlay[];
  /** Public holiday overlays to display in header and All Day row (optional) */
  publicHolidays?: PublicHolidayOverlay[];
  /** When true, leave badges navigate to /leave instead of /manager/leave */
  isEmployeeView?: boolean;
  /** When true, highlights the full time range of each shift with its color (My Schedule view) */
  highlightShiftDuration?: boolean;
  /** Company IANA timezone (e.g. "Europe/Berlin"). Defaults to "UTC". */
  companyTimezone?: string;
}

// Helper functions for smart hour collapse
function hourFloor(d: Date) { 
  return d.getHours(); 
}

function hourCeil(d: Date) { 
  return d.getMinutes() > 0 ? d.getHours() + 1 : d.getHours(); 
}

function getVisibleHourRange(
  weekShifts: Array<{ start_time: string; end_time: string }>,
  tz: string
) {
  if (!weekShifts.length) return null;

  let earliest = 23, latest = 0;
  for (const s of weekShifts) {
    const startH = getCompanyHour(s.start_time, tz);
    const endH = getCompanyHour(s.end_time, tz);
    const endM = getCompanyMinute(s.end_time, tz);
    const ceilEnd = endM > 0 ? endH + 1 : endH;
    earliest = Math.min(earliest, startH);
    latest = Math.max(latest, ceilEnd);
  }
  
  // clamp to [0, 23]; ensure at least one row
  earliest = Math.max(0, Math.min(earliest, 23));
  latest = Math.max(earliest, Math.min(latest, 23));
  
  // Optional: 1-hour padding for context (set to 0 for now)
  const PAD = 0;
  const visibleStart = Math.max(0, earliest - PAD);
  const visibleEnd = Math.min(23, latest + PAD);
  
  return { visibleStart, visibleEnd };
}

export function WeekView({ currentWeek, onWeekChange, shifts, onTimeSlotClick, onShiftsUpdated, profiles = [], onShiftClick, onCreateShift, leaveOverlays = [], publicHolidays = [], isEmployeeView = false, highlightShiftDuration = false, companyTimezone = 'UTC' }: WeekViewProps) {
  const isMobile = useIsMobile();
  const tz = companyTimezone;
  
  // The desktop 8-column grid needs ~800px min-width, so use card layout below lg (1024px)
  const [isNarrow, setIsNarrow] = useState(typeof window !== 'undefined' ? window.innerWidth < 1024 : false);
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 1023px)');
    const onChange = () => setIsNarrow(mql.matches);
    mql.addEventListener('change', onChange);
    setIsNarrow(mql.matches);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  const { user, profile } = useAuth();
  
  
  const canEdit = useHasPermission('add_edit_shifts');
  // All authenticated users can interact with shifts (view details, click)
  const canInteract = !!user;
  
  // Enhanced status display logic as per investigation plan
  const getShiftDisplayInfo = (shift: Shift) => {
    // Check for pending swap status first
    if (shift.swap_status && ['open', 'accepted', 'manager_pending'].includes(shift.swap_status)) {
      return {
        label: shift.employee_name,
        className: "border-dashed bg-yellow-50 text-yellow-800 border-yellow-300",
        swapStatus: shift.swap_status,
        showSwapIndicator: true
      };
    }
    
    if (shift.status === 'open' || !shift.profile_id) {
      // Check if this is an offered shift
      if (shift.offered_by_user_id && shift.offered_by_name) {
        return {
          label: `Offered by ${shift.offered_by_name}`,
          className: "border-dashed bg-orange-50 text-orange-700 border-orange-300"
        };
      } else {
        return {
          label: "Open shift",
          className: "border-dashed bg-muted/40 text-muted-foreground border-muted-foreground/30"
        };
      }
    } else {
      // Assigned shift - use shift code color
      const color = shift.shift_code_info?.color || '#94a3b8';
      return {
        label: shift.employee_name,
        color: color
      };
    }
  };
  
  const getShiftsForDay = (date: Date) => {
    return shifts.filter(shift => 
      !shift.is_all_day && isSameCompanyDay(shift.start_time, date, tz)
    );
  };
  
  const getAllDayShiftsForDay = (date: Date) => {
    return shifts.filter(shift => 
      shift.is_all_day && isSameCompanyDay(shift.start_time, date, tz)
    );
  };
  
  // Get leave overlays for a specific day
  const getLeavesForDay = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return leaveOverlays.filter(leave => {
      const start = leave.start_date;
      const end = leave.end_date;
      return dateStr >= start && dateStr <= end;
    });
  };

  // Get public holiday for a specific day
  const getHolidayForDay = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return publicHolidays.find(h => h.date === dateStr);
  };

  const weekDays = eachDayOfInterval({
    start: startOfWeek(currentWeek),
    end: endOfWeek(currentWeek)
  });

  // Smart hour collapse: compute visible hour range
  const allHours = Array.from({ length: 24 }, (_, i) => i);
  const range = getVisibleHourRange(shifts, tz);
  const hasShifts = !!range;

  const visibleStart = range?.visibleStart ?? 0;
  const visibleEnd = range?.visibleEnd ?? 23;
  const visibleHours = hasShifts ? allHours.slice(visibleStart, visibleEnd + 1) : [];

  if (isNarrow) {
    // Mobile/Tablet: Show stacked day cards
    return (
      <div className="space-y-4">
        {/* Week Navigation */}
        <div className="flex items-center justify-between">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => onWeekChange(subWeeks(currentWeek, 1))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="text-center">
            <div className="font-semibold text-sm">
              {format(startOfWeek(currentWeek), 'MMM d')} - {format(endOfWeek(currentWeek), 'MMM d')}
            </div>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => onWeekChange(addWeeks(currentWeek, 1))}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Days Grid - Mobile */}
        <div className="space-y-3">
          {weekDays.map(day => {
            const dayShifts = getShiftsForDay(day);
            const dayLeaves = getLeavesForDay(day);
            return (
              <Card key={day.toISOString()}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <div>
                      <div>{format(day, 'EEE')}</div>
                      <div className="text-xs text-muted-foreground font-normal">
                        {format(day, 'MMM d')}
                      </div>
                      {(() => {
                        const holiday = getHolidayForDay(day);
                        return holiday ? (
                          <div className="text-[10px] text-rose-600 dark:text-rose-400 font-medium flex items-center justify-center gap-0.5 mt-0.5">
                            <Star className="h-2.5 w-2.5 fill-current" />
                            <span className="truncate max-w-[60px]">{holiday.name}</span>
                          </div>
                        ) : null;
                      })()}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onTimeSlotClick(day, 9)}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  {/* Public Holiday - Mobile */}
                  {(() => {
                    const holiday = getHolidayForDay(day);
                    return holiday ? (
                      <div className="mb-3 pb-3 border-b">
                        <PublicHolidayBadge holiday={holiday} />
                      </div>
                    ) : null;
                  })()}
                  
                  {/* Leave Overlays - Mobile */}
                  {dayLeaves.length > 0 && (
                    <div className="space-y-1 mb-3 pb-3 border-b">
                      {dayLeaves.map(leave => (
                        <LeaveOverlayBadge key={leave.leave_request_id} leave={leave} isEmployeeView={isEmployeeView} />
                      ))}
                    </div>
                  )}
                  
                  {dayShifts.length > 0 ? (
                    <div className="space-y-2">
                  {dayShifts.map(shift => {
                        const displayInfo = getShiftDisplayInfo(shift);
                        
                        return (
                          <div
                            key={shift.id}
                            className={`p-2 rounded-md text-xs ${displayInfo.className || ''} ${canInteract ? 'cursor-pointer hover:opacity-80' : ''} relative`}
                            style={displayInfo.color ? {
                              backgroundColor: `${displayInfo.color}15`,
                              borderLeft: `4px solid ${displayInfo.color}`,
                              color: displayInfo.color
                            } : undefined}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (canInteract && onShiftClick) {
                                onShiftClick(shift);
                              }
                            }}
                          >
                            <div className="font-medium flex items-center gap-1">
                              {displayInfo.showSwapIndicator && <Clock className="h-3 w-3" />}
                              {displayInfo.label}
                            </div>
                            <div>
                              {formatInCompanyTz(shift.start_time, tz, 'h:mm a')} - 
                              {formatInCompanyTz(shift.end_time, tz, 'h:mm a')}
                            </div>
                            {displayInfo.showSwapIndicator && (
                              <div className="text-xs font-medium">Swap Pending</div>
                            )}
                            {shift.note && <div className="mt-1">{shift.note}</div>}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-xs text-muted-foreground text-center py-2">
                      No shifts
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    );
  }

  // Desktop: Traditional calendar grid
  return (
    <div className="space-y-4">
      {/* Week Navigation */}
      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={() => onWeekChange(subWeeks(currentWeek, 1))}>
          <ChevronLeft className="h-4 w-4 mr-2" />
          Previous Week
        </Button>
        <div className="text-center">
          <div className="font-semibold">
            Week of {format(startOfWeek(currentWeek), 'MMM d, yyyy')}
          </div>
        </div>
        <Button variant="outline" onClick={() => onWeekChange(addWeeks(currentWeek, 1))}>
          Next Week
          <ChevronRight className="h-4 w-4 ml-2" />
        </Button>
      </div>

      {/* Public holidays in empty-state week */}
      {!hasShifts && publicHolidays.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Star className="h-4 w-4 text-rose-600 dark:text-rose-400 fill-current" />
              <span className="text-sm font-medium text-rose-700 dark:text-rose-300">Public Holidays This Week</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {publicHolidays.map(h => (
                <PublicHolidayBadge key={h.date} holiday={h} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty State - No Shifts */}
      {!hasShifts && onCreateShift && (
        <Card>
          <CardContent className="py-16 text-center">
            <div className="flex items-center justify-center">
              <Button
                className="inline-flex items-center gap-2 rounded-2xl px-6 py-4 bg-primary text-primary-foreground shadow"
                onClick={() => {
                  onCreateShift("empty-week", startOfWeek(currentWeek).toISOString(), endOfWeek(currentWeek).toISOString());
                }}
              >
                <Plus className="h-4 w-4" />
                Create New Shift
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Calendar Grid - Only show when there are shifts */}
      {hasShifts && (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <div 
                className="grid grid-cols-8 gap-0 min-w-[800px]"
                style={{
                  gridTemplateRows: `auto auto repeat(${visibleHours.length}, minmax(3rem, auto))`,
                }}
              >
                {/* Header row */}
                <div className="p-3 font-medium text-center border-b bg-muted/50">Time</div>
                {weekDays.map(day => {
                  const holiday = getHolidayForDay(day);
                  return (
                    <div key={day.toISOString()} className={`p-3 font-medium text-center border-l border-b bg-muted/50 ${holiday ? 'bg-rose-50/50 dark:bg-rose-950/20' : ''}`}>
                      <div>{format(day, 'EEE')}</div>
                      <div className="text-sm text-muted-foreground">{format(day, 'MMM d')}</div>
                      {holiday && (
                        <div className="text-[10px] text-rose-600 dark:text-rose-400 font-medium flex items-center justify-center gap-0.5 mt-0.5">
                          <Star className="h-2.5 w-2.5 fill-current" />
                          <span className="truncate">{holiday.name}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
                
                {/* All-day shifts row */}
                <div className="p-2 text-xs text-muted-foreground border-b bg-muted/30">All Day</div>
                {weekDays.map(day => {
                  const allDayShifts = getAllDayShiftsForDay(day);
                  const dayLeaves = getLeavesForDay(day);
                  return (
                    <div 
                      key={`allday-${day.toISOString()}`} 
                      className="p-1 border-l border-b min-h-[40px] bg-muted/10"
                    >
                      {/* Public Holiday */}
                      {(() => {
                        const holiday = getHolidayForDay(day);
                        return holiday ? (
                          <div className="mb-1">
                            <PublicHolidayBadge holiday={holiday} compact />
                          </div>
                        ) : null;
                      })()}
                      
                      {/* Leave Overlays */}
                      {dayLeaves.map(leave => (
                        <div key={leave.leave_request_id} className="mb-1">
                          <LeaveOverlayBadge leave={leave} compact isEmployeeView={isEmployeeView} />
                        </div>
                      ))}
                      
                      {/* All Day Shifts */}
                      {allDayShifts.map(shift => {
                        const bgColor = shift.shift_code_info?.color || '#94a3b8';
                        return (
                          <div
                            key={shift.id}
                            className="text-xs px-2 py-1 rounded mb-1 cursor-pointer hover:opacity-80 transition-opacity"
                            style={{ 
                              backgroundColor: `${bgColor}20`, 
                              borderLeft: `3px solid ${bgColor}`,
                              color: bgColor
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (canInteract && onShiftClick) {
                                onShiftClick(shift);
                              }
                            }}
                          >
                            <div className="font-medium">{shift.employee_name}</div>
                            <div className="text-xs opacity-80">{shift.shift_code_info?.name || shift.shift_code}</div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}

                {/* Time slots - only visible hours */}
                {visibleHours.map(hour => (
                  <div key={hour} className="contents">
                    <div className="p-2 text-sm text-muted-foreground border-b bg-muted/30">
                      {format(new Date().setHours(hour, 0, 0, 0), 'h:mm a')}
                    </div>
                    {weekDays.map(day => {
                      const dayShifts = getShiftsForDay(day);
                      const hourShifts = dayShifts.filter(shift => {
                        return getCompanyHour(shift.start_time, tz) === hour;
                      });
                      
                      // Check if this hour cell is covered by any shift (for duration highlighting)
                      const coveringShift = highlightShiftDuration ? dayShifts.find(shift => {
                        const startHour = getCompanyHour(shift.start_time, tz);
                        const endH = getCompanyHour(shift.end_time, tz);
                        const endM = getCompanyMinute(shift.end_time, tz);
                        const endHour = endH + (endM > 0 ? 1 : 0);
                        return hour >= startHour && hour < endHour;
                      }) : null;
                      
                      // Use shift_code_info color, or fallback to a default for manual/custom shifts
                      const highlightColor = coveringShift 
                        ? (coveringShift.shift_code_info?.color || '#6366f1') // Fallback to primary color
                        : null;

                      return (
                        <div
                          key={`${day.toISOString()}-${hour}`}
                          className="p-1 border-l border-b min-h-[64px] hover:bg-muted/50 cursor-pointer relative"
                          style={highlightColor ? {
                            backgroundColor: `${highlightColor}12`,
                          } : undefined}
                          onClick={() => {
                            if (onCreateShift) {
                              const pad = (n: number) => String(n).padStart(2, "0");
                              const startHH = `${pad(hour)}:00`;
                              const endHH = `${pad((hour + 1) % 24)}:00`;
                              
                              onCreateShift(
                                "cell",
                                undefined, // weekStartISO
                                undefined, // weekEndISO
                                day.toISOString(), // initialDateISO
                                startHH, // initialStartHHmm
                                endHH // initialEndHHmm
                              );
                            } else {
                              onTimeSlotClick(day, hour); // Fallback to old behavior
                            }
                          }}
                        >
                          {hourShifts.map(shift => {
                            const displayInfo = getShiftDisplayInfo(shift);
                            
                            return (
                              <div
                                key={shift.id}
                                className={`text-xs p-1 rounded mb-1 ${displayInfo.className || ''} ${canInteract ? 'cursor-pointer hover:opacity-80' : ''} relative group`}
                                style={displayInfo.color ? {
                                  backgroundColor: `${displayInfo.color}15`,
                                  borderLeft: `3px solid ${displayInfo.color}`,
                                  color: displayInfo.color
                                } : undefined}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (canInteract && onShiftClick) {
                                    onShiftClick(shift);
                                  }
                                }}
                              >
                                <div className="flex items-center gap-1">
                                  {displayInfo.showSwapIndicator && <Clock className="h-3 w-3 flex-shrink-0" />}
                                  <TruncatedTextTooltip 
                                    text={displayInfo.label}
                                    className="font-medium"
                                  />
                                </div>
                                <div>
                                  {formatInCompanyTz(shift.start_time, tz, 'h:mm a')} - 
                                  {formatInCompanyTz(shift.end_time, tz, 'h:mm a')}
                                </div>
                                {displayInfo.showSwapIndicator && (
                                  <div className="text-xs font-medium">Swap Pending</div>
                                )}
                                {shift.note && <div className="mt-1 text-xs">{shift.note}</div>}
                                
                                {/* Plus icon for creating shift at same time */}
                                {canEdit && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="absolute top-0 right-0 h-4 w-4 p-0 opacity-0 group-hover:opacity-100 bg-white/80 hover:bg-white"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const startHH = formatInCompanyTz(shift.start_time, tz, 'HH');
                                      const startMM = formatInCompanyTz(shift.start_time, tz, 'mm');
                                      const endHH = formatInCompanyTz(shift.end_time, tz, 'HH');
                                      const endMM = formatInCompanyTz(shift.end_time, tz, 'mm');
                                      
                                      if (onCreateShift) {
                                        onCreateShift(
                                          "cell",
                                          undefined, // weekStartISO
                                          undefined, // weekEndISO
                                          day.toISOString(), // initialDateISO
                                          `${startHH}:${startMM}`, // initialStartHHmm
                                          `${endHH}:${endMM}` // initialEndHHmm
                                        );
                                      }
                                    }}
                                  >
                                    <Plus className="h-3 w-3" />
                                  </Button>
                                )}
                              </div>
                            );
                          })}
                          {hourShifts.length === 0 && (
                            <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100">
                              <Plus className="h-4 w-4 text-muted-foreground" />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
